import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import {
    EnrichedIntellectualChildrenValidity,
    IntellectualChildrenValidity,
    InvalidIntellectualChildrenDialogOutput
} from '@common/entities/dialog/invalid-intellectual-children-dialog.entity';

import {
    ActionItem,
    ActionItemStyle,
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    QueryPart,
    QueryPartsToStructuredQuery,
    QueryPartStrategy,
    RecordStatus,
    UrlQueryParamsConverter
} from '@stylehaven/components';
@Component({
    standalone: false,
    selector: 'mhv-invalid-intellectual-children-dialog',
    templateUrl: './invalid-intellectual-children-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvalidIntellectualChildrenDialogComponent extends BaseContainer implements OnInit {
    public warningMsg: string;
    public validityObjects: EnrichedIntellectualChildrenValidity[];

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<InvalidIntellectualChildrenDialogOutput>
    ) {
        super();
    }

    ngOnInit(): void {
        this.initValidityObjects();
    }

    private initValidityObjects(): void {
        this.warningMsg = this.getWarningMessage(this.config.data.intellectualRecordValidityObjects);
        this.validityObjects = this.config.data.intellectualRecordValidityObjects.map(
            (validityObject: IntellectualChildrenValidity) => ({
                ...validityObject,
                message: this.getIntellectualRecordMessage(
                    validityObject.intellectualRecord,
                    validityObject.nrOfInvalidChildren
                ),
                link: this.getInvalidChildrenLink(validityObject.intellectualRecord)
            })
        );
    }

    getWarningMessage(validityObjects: IntellectualChildrenValidity[]): string {
        const type: string = validityObjects.every(validityObject =>
            validityObject.intellectualRecord.isNewCollection()
        )
            ? 'collection'
            : 'generic';

        return this.translateService.translate(`intellectual.invalid-children-dialog.warning.${type}`);
    }

    private getIntellectualRecordMessage(intellectualRecord: ApiFragment, nrOfInvalidChildren: number): string {
        const type: string = intellectualRecord.isNewCollection() ? 'collection' : 'generic';

        return this.translateService.translate(`intellectual.invalid-children-dialog.message.${type}`, {
            title: intellectualRecord.getTitle(),
            amount: nrOfInvalidChildren
        });
    }

    private getInvalidChildrenLink(intellectualRecord: ApiFragment): ActionItem | null {
        const type: string = intellectualRecord.isNewCollection() ? 'collection' : 'generic';

        const queryParts: QueryPart[] = [
            new QueryPart({
                field: `Structural.ReferenceCodes.${intellectualRecord.getRecordType()}`,
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [intellectualRecord.getRecordId()]
            }),
            new QueryPart({
                field: 'Administrative.RecordStatus',
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [RecordStatus.DraftInvalid, RecordStatus.Processing, RecordStatus.Rejected]
            })
        ];

        return {
            label: this.translateService.translate(`intellectual.invalid-children-dialog.view-invalid-items.${type}`),
            style: ActionItemStyle.Link,
            navigationLink: {
                route: `/all`,
                target: '_blank',
                extras: {
                    queryParams: UrlQueryParamsConverter.generateQueryParams(QueryPartsToStructuredQuery(queryParts))
                }
            }
        };
    }

    public cancel(): void {
        const output: InvalidIntellectualChildrenDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }
}
